import React, { useState, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../api/client';
import { useNotification } from '../components/Notification';
import './AdminFooterLinks.css';

function generateId() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2);
}

const LOCALES = [
  { code: 'lt', label: 'Lietuvių' },
  { code: 'en', label: 'English' },
  { code: 'de', label: 'Deutsch' },
];

const DEFAULT_ITEMS = [
  { id: generateId(), label_lt: 'Pradžia', label_en: 'Home', label_de: 'Start', type: 'url', pageSlug: '', url: '/' },
  { id: generateId(), label_lt: 'Produktai', label_en: 'Products', label_de: 'Produkte', type: 'url', pageSlug: '', url: '/products' },
];

function normalizeItem(item) {
  const label = item.label ?? item.label_lt ?? '';
  return {
    ...item,
    label_lt: item.label_lt ?? label,
    label_en: item.label_en ?? '',
    label_de: item.label_de ?? '',
  };
}

function AdminHeaderMenu() {
  const queryClient = useQueryClient();
  const notification = useNotification();
  const [items, setItems] = useState([]);
  const [activeLang, setActiveLang] = useState('lt');

  const { data, isLoading } = useQuery({
    queryKey: ['admin-header-menu'],
    queryFn: async () => {
      const res = await apiClient.get('/admin/header-menu');
      return res.data;
    },
  });

  const { data: pages = [] } = useQuery({
    queryKey: ['admin-pages'],
    queryFn: async () => {
      const res = await apiClient.get('/admin/pages');
      return res.data;
    },
  });

  useEffect(() => {
    if (data?.content) {
      const saved = data.content.items;
      if (Array.isArray(saved) && saved.length > 0) {
        setItems(saved.map(normalizeItem));
      } else {
        setItems(JSON.parse(JSON.stringify(DEFAULT_ITEMS)));
      }
    }
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: async (payload) => {
      await apiClient.put('/admin/header-menu', { content: payload });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-header-menu']);
      queryClient.invalidateQueries(['header-menu-public']);
      notification.success('Viršutinio meniu nuorodos išsaugotos');
    },
    onError: (error) => {
      notification.error(error.response?.data?.error || 'Klaida išsaugant');
    },
  });

  const addItem = useCallback(() => {
    setItems((prev) => [
      ...prev,
      { id: generateId(), label_lt: '', label_en: '', label_de: '', type: 'url', pageSlug: '', url: '' },
    ]);
  }, []);

  const updateItem = useCallback((linkId, patch) => {
    setItems((prev) => prev.map((l) => (l.id === linkId ? { ...l, ...patch } : l)));
  }, []);

  const removeItem = useCallback((linkId) => {
    setItems((prev) => prev.filter((l) => l.id !== linkId));
  }, []);

  const moveItem = useCallback((fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= items.length) return;
    setItems((prev) => {
      const next = [...prev];
      const [removed] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, removed);
      return next;
    });
  }, [items.length]);

  const loadDefault = useCallback(() => {
    setItems(JSON.parse(JSON.stringify(DEFAULT_ITEMS)));
    notification.success('Numatytos nuorodos įkeltos (LT/EN/DE). Išsaugokite, jei norite jas naudoti.');
  }, [notification]);

  const handleSave = useCallback(() => {
    saveMutation.mutate({ items });
  }, [items, saveMutation]);

  if (isLoading) {
    return (
      <div className="admin-footer-links-page container">
        <div className="admin-footer-links-loading">Kraunama...</div>
      </div>
    );
  }

  return (
    <div className="admin-footer-links-page container">
      <div className="footer-links-header">
        <h1>Viršutinio meniu nuorodos</h1>
        <div className="footer-links-actions">
          <button type="button" className="btn btn-secondary" onClick={loadDefault}>
            Įkelti numatytą meniu
          </button>
          <Link to="/" target="_blank" rel="noopener noreferrer" className="btn btn-secondary">
            Peržiūrėti puslapį
          </Link>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSave}
            disabled={saveMutation.isLoading}
          >
            {saveMutation.isLoading ? 'Išsaugoma...' : 'Išsaugoti'}
          </button>
        </div>
      </div>

      <p className="footer-links-hint">
        Nuorodos rodomos viršutiniame meniu (šalia logotipo). Užpildykite pavadinimus lietuvių, anglų ir vokiečių kalbomis. Eiliškumą keiskite rodyklėmis.
      </p>

      <div className="admin-lang-tabs">
        {LOCALES.map(({ code, label }) => (
          <button
            key={code}
            type="button"
            className={`admin-lang-tab ${activeLang === code ? 'active' : ''}`}
            onClick={() => setActiveLang(code)}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="footer-links-legal-card">
        <h3 className="footer-links-legal-title">Meniu nuorodos (pavadinimas – {LOCALES.find((l) => l.code === activeLang)?.label})</h3>
        <p className="footer-links-legal-hint">
          Šios nuorodos atsiranda viršutiniame navigacijos meniu centre. Redaguokite pavadinimus kiekvienai kalbai atskirai.
        </p>
        {items.length === 0 ? (
          <p className="footer-links-empty-msg">Nuorodų nėra. Pridėkite nuorodą arba įkelkite numatytą meniu.</p>
        ) : (
          <div className="footer-links-table-wrap">
            <table className="footer-links-table">
              <thead>
                <tr>
                  <th className="col-move">Eilė</th>
                  <th className="col-label">Pavadinimas</th>
                  <th className="col-type">Tipas</th>
                  <th className="col-target">Puslapis / URL</th>
                  <th className="col-remove"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((link, linkIndex) => (
                  <tr key={link.id}>
                    <td className="col-move">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        title="Aukštyn"
                        disabled={linkIndex === 0}
                        onClick={() => moveItem(linkIndex, linkIndex - 1)}
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        title="Žemyn"
                        disabled={linkIndex === items.length - 1}
                        onClick={() => moveItem(linkIndex, linkIndex + 1)}
                      >
                        ↓
                      </button>
                    </td>
                    <td className="col-label">
                      <input
                        type="text"
                        value={link[`label_${activeLang}`] ?? ''}
                        onChange={(e) => updateItem(link.id, { [`label_${activeLang}`]: e.target.value })}
                        placeholder={activeLang === 'lt' ? 'Pavadinimas (LT)' : activeLang === 'en' ? 'Label (EN)' : 'Bezeichnung (DE)'}
                      />
                    </td>
                    <td className="col-type">
                      <select
                        value={link.type || 'url'}
                        onChange={(e) =>
                          updateItem(link.id, {
                            type: e.target.value,
                            pageSlug: e.target.value === 'page' ? (pages[0]?.slug || '') : '',
                            url: e.target.value === 'url' ? link.url || '' : '',
                          })
                        }
                      >
                        <option value="url">Nuoroda (URL)</option>
                        <option value="page">Sistemos puslapis</option>
                      </select>
                    </td>
                    <td className="col-target">
                      {link.type === 'page' ? (
                        <select
                          value={link.pageSlug || ''}
                          onChange={(e) => updateItem(link.id, { pageSlug: e.target.value })}
                        >
                          <option value="">— Pasirinkite puslapį —</option>
                          {pages.map((p) => (
                            <option key={p.id} value={p.slug}>
                              {p.title_lt || p.slug}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="text"
                          value={link.url || ''}
                          onChange={(e) => updateItem(link.id, { url: e.target.value })}
                          placeholder="/kelias arba https://..."
                        />
                      )}
                    </td>
                    <td className="col-remove">
                      <button
                        type="button"
                        className="btn-remove"
                        onClick={() => removeItem(link.id)}
                        title="Pašalinti nuorodą"
                        aria-label="Pašalinti"
                      >
                        ×
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <button type="button" className="btn btn-secondary footer-links-add-legal" onClick={addItem}>
          + Pridėti nuorodą
        </button>
      </div>
    </div>
  );
}

export default AdminHeaderMenu;
